import { IRListItem } from '@rhybus/api'

import {
  ALL_SELECT_VALUE,
  ALL_SELECT_VALUE_STRING,
} from '../../../../utils/constants/global-constants'
import { PersonDetails } from '../../../../utils/constants/global-interfaces'
import { mpfRListsHttp } from '../../api/mpf-r-lists-http'
import { mpfRViewsHttp } from '../../api/mpf-views-http'
import { formatStringForServer } from '../../formatting/formatting'
import { FilteredDatesType, getFilteredDates } from '../../general/general'
import { and, isArchivedFilter, or } from '../data'
import { STATUS_CODE } from './dueout-status-metadata'


interface DueOut {
  ArchivedDate?: Date | null | string
  'AssignedComponent/MetadataValue'?: string
  AssignedComponentCode: number
  AssignedTo?: string
  'AssignedTo/edipi'?: string
  'AssignedTo/email'?: string
  'AssignedTo/firstname'?: string
  'AssignedTo/lastname'?: string
  CanceledDate?: Date | null | string
  'Category/MetadataValue'?: string
  CategoryCode: null | number
  CategoryValue? : string
  ClosedDate?: Date | null | string
  Customer?: PersonDetails | string
  'Customer/edipi'?: string
  'Customer/email'?: string
  'Customer/firstname'?: string
  'Customer/lastname'?: string
  CustomerStatus: string
  DOS: Date | string
  Description: string
  DoDNumber: string
  DueDate?: Date | null | string
  DueOutGUID?: string
  DueOutID?: string
  DutyEmail: string
  DutyPhone: string
  FY?: number
  ID?: number
  Installation: string
  InvolvedUsers?: string[]
  IsArchived?: boolean
  ItemCreated?: Date
  ItemCreatedBy?: string
  'ItemCreatedBy/edipi'?: string
  'ItemCreatedBy/email'?: string
  'ItemCreatedBy/firstname'?: string
  'ItemCreatedBy/lastname'?
  ItemModified?: Date
  ItemModifiedBy?: string
  'ItemModifiedBy/edipi'?: string
  'ItemModifiedBy/email'?: string
  'ItemModifiedBy/firstname'?: string
  'ItemModifiedBy/latname'?: string
  OrgMAJCOM: string
  Organization: string
  PASCODE: string
  PASCODEServicedBy?: string
  PDD: Date | string
  PRP: string
  PersonalEmail?: string
  PersonalPhoneNumber?: string
  PersonalPhoneProvider?: string
  'PreviousCategory/MetadataValue'?: string
  PreviousCategoryCode?: null | number
  'PreviousStatus/MetadataValue'?: string
  PreviousStatusCode?: string
  'PreviousSubCategory/MetadataValue'?
  PreviousSubCategoryCode?: null | number
  Priority?: string
  RNLTD: Date | string
  Rank: string
  'Section/MetadataValue'?: string
  SectionCode: null | number
  SectionValue? : string
  ServicingBy?: string
  'ServicingMAJCOM/MetadataValue'?: string
  ServicingMAJCOMCode: number
  ServicingOrganization?: string
  Status?:string
  'Status/StatusDescription'?: string
  StatusCode?: string
  'SubCategory/MetadataValue'?: string
  SubCategoryCode?: null | number
  SubCategoryValue ? : string
  Subject: string
  TeamMembers?: PersonDetails[]
  eKey?: string
  eTag?: string
}

interface DueOutCount {
  DueOutGUID_count: number
}

interface DueOutCategoryCount {
  DueOutGUID_count: number
  category: string
}

interface DueOutStatusCount {
  DueOutGUID_count: number
  StatusCode: string
}
interface DueOutPriorityCount {
  DueOutGUID_count: number
  Priority: string
}
interface LoginRoles {
  CSSPASCODEFilter?: string[] 
  usersAssignedComponentCode?: number[]
  usersMajcomCodeFilter ?: number[] 
  usersSectionCodeFilter?: number[] 
  usersServicingOrganizationFilter?:  string[] 
  usersServicingOrganizationWithoutSectionFilter?:  string[] 
}
interface DueOutDueDateCount {
  DueDate: string
  DueOutGUID_count: number
}
interface DueOutFetchArgs {
  archivedFilter?: string
  archivedsearchFilter?: string
  assignedComponentCodeFilter?: number
  categories?: any
  categoryCodeFilter?: number
  closedFilter?: string
  distinctFiltersToQuery?: string[] // used only in getDueOutFilters
  dueDateLessThanDateIgnoreTime?: Date
  dueOutDOSFilter?: null | string
  dueOutDueDateFilter?: null | string
  dueOutPDDFilter?: null | string
  dueOutPRPFilter?: null | string
  dueOutRNLTDFilter?: null | string
  dueoutGuid?: string
  groupBy?: string
  hasClosedAndCanceled?: boolean
  isArchivedFilter?: false | null | true
  isAscending?: boolean
  isSavedDueoutDeleted?: boolean
  isViewApplied?: boolean
  morethanTenDaysAgoDueDateCount?: boolean
  myDueouts?: string
  onePageOnly?: boolean
  orderBy?: string
  pageNumber?: number // when set, fetch starting at this page
  pascode?: string[]
  priorityFilter?: null | string
  refresh?: boolean
  relatedUserFilter?: null | string
  savedFilter?: boolean
  searchFilter?: null | string
  sectionCodeFilter?: null | number
  servicingByFilter?: string
  servicingMAJCOMCodeFilter?: number
  servicingOrganizationFilter?: number | string
  sevevenDaysAgoDueDateCount?: boolean
  statusCodesFilter?: string[]
  statusCodesOmittedFilter?: string[]
  subCategoryCodeFilter?: number
  submittedFilter?: null | string
  tenDaysAgoDueDateCount?: boolean
  threeDaysAgoDueDateCount?: boolean
  userLoginRole?: LoginRoles
  waitOnParentDependency?:boolean 
  yearFilter?: number
}

// Warning -- page size should never be set so low that a full page of
// inquiries is displayed in the table with no scrollbar present.
// If this happens the user won't have a way to trigger additional page
// loads on scroll.
const PAGE_SIZE = 100

const LIST_NAME = 'DueOutList'
const VIEW_NAME = 'V_DueOutList'


const COLUMN_NAMES = {
  ArchivedDate: 'ArchivedDate',
  AssignedComponent: 'AssignedComponent/MetadataValue',
  AssignedComponentCode: 'AssignedComponentCode',
  AssignedTo: 'AssignedTo',
  AssignedToEDIPI: 'AssignedTo/edipi',
  AssignedToEmail: 'AssignedTo/email',
  AssignedToFirstName: 'AssignedTo/firstname',
  AssignedToLastName: 'AssignedTo/lastname',
  CanceledDate: 'CanceledDate',
  Category: 'Category/MetadataValue',
  CategoryCode: 'CategoryCode',
  ClosedDate: 'ClosedDate',
  Customer: 'Customer',
  CustomerEDIPI: 'Customer/edipi',
  CustomerEmail: 'Customer/email',
  CustomerFirstName: 'Customer/firstname',
  CustomerLastName: 'Customer/lastname',
  CustomerStatus: 'CustomerStatus',
  DOS: 'DOS',
  Description: 'Description',
  DoDNumber: 'DoDNumber',
  DueDate: 'DueDate',
  DueOutGUID: 'DueOutGUID',
  DueOutID: 'DueOutID',
  DutyEmail: 'DutyEmail',
  DutyPhone: 'DutyPhone',
  FY: 'FY',
  Installation: 'Installation',
  InvolvedUsers: 'InvolvedUsers',
  IsArchived: 'IsArchived',
  ItemCreated: 'ItemCreated',
  ItemCreatedBy: 'ItemCreatedBy',
  ItemCreatedByEDIPI: 'ItemCreatedBy/edipi',
  ItemCreatedByEmail: 'ItemCreatedBy/email',
  ItemCreatedByFirstName: 'ItemCreatedBy/firstname',
  ItemCreatedByLastName: 'ItemCreatedBy/lastname',
  ItemModified: 'ItemModified',
  ItemModifiedBy: 'ItemModifiedBy',
  ItemModifiedByEDIPI: 'ItemModifiedBy/edipi',
  ItemModifiedByEmail: 'ItemModifiedBy/email',
  ItemModifiedByFirstName: 'ItemModifiedBy/firstname',
  ItemModifiedByLastName: 'ItemModifiedBy/lastname',
  OrgMAJCOM: 'OrgMAJCOM',
  Organization: 'Organization',
  PASCODE: 'PASCODE',
  PASCODEServicedBy: 'PASCODEServicedBy',
  PDD: 'PDD',
  PRP: 'PRP',
  PascodeID: 'PascodeID',
  PersonalEmail: 'PersonalEmail',
  PersonalPhoneNumber: 'PersonalPhoneNumber',
  PersonalPhoneProvider: 'PersonalPhoneProvider',
  PreviousCategory: 'PreviousCategory/MetadataValue',
  PreviousCategoryCode: 'PreviousCategoryCode',
  PreviousStatus: 'PreviousStatus/StatusDescription',
  PreviousStatusCode: 'PreviousStatusCode',
  PreviousSubCategory: 'PreviousSubCategory/MetadataValue',
  PreviousSubCategoryCode: 'PreviousSubCategoryCode',
  Priority: 'Priority',
  RNLTD: 'RNLTD',
  Rank: 'Rank',
  Section: 'Section/MetadataValue',
  SectionCode: 'SectionCode',
  ServicingBy: 'ServicingBy',
  ServicingMAJCOM: 'ServicingMAJCOM/MetadataValue',
  ServicingMAJCOMCode: 'ServicingMAJCOMCode',
  ServicingOrganization: 'ServicingOrganization',
  Status: 'Status/StatusDescription',
  StatusCode: 'StatusCode',
  SubCategory: 'SubCategory/MetadataValue',
  SubCategoryCode: 'SubCategoryCode',
  Subject: 'Subject',
  TeamMembers: 'TeamMembers',
  eKey: 'eKey',
  eTag: 'eTag',
}

const getDueOuts = async (args: DueOutFetchArgs): Promise<DueOut[]> => {
  try {
    const list = await mpfRListsHttp().getList(LIST_NAME)
    const filter = constructDueOutFilter(args)
    const pageNum = args.pageNumber ?? -1
    const onePageOnly = args.onePageOnly ?? true
    const orderBy = args.orderBy ?? COLUMN_NAMES.ItemModified
    const ascending = args?.isAscending ?? false

    const query = list
      .query()
      .select(...Object.values(COLUMN_NAMES))
      .filter(filter)
      .expand(
        'AssignedComponent',
        'AssignedTo',
        'Category',
        'Customer',
        'ItemCreatedBy',
        'ItemModifiedBy',
        'PreviousCategory',
        'PreviousSubCategory',
        'Section',
        'Status',
        'SubCategory',
        'TeamMembers'
      )
      .orderBy([{ ascending, orderBy }])

    // If a page number is supplied, skip to fetch starting at that page number
    const startAtPageQuery = pageNum > -1 ? query.skip(pageNum * PAGE_SIZE) : query

    // If onePageOnly is set to true, only fetch one page of data, otherwise
    // fetch all
    const oneOrManyPagesQuery = onePageOnly ? startAtPageQuery.top(PAGE_SIZE) : startAtPageQuery

    const items = await oneOrManyPagesQuery.get()
    const convertedItems = [...items].map((i) => convertDatesInServerObject(i))

    return convertedItems
  } catch (error) {
    // Handle the error here
    console.error('An error occurred:', error)
    return []
  }
}

const getDueOutsViews = async (args: DueOutFetchArgs): Promise<DueOut[]> => {
  const list = await mpfRViewsHttp().getview(VIEW_NAME)
  const filter = constructDueOutFilter(args, true)
  const pageNum = args.pageNumber ?? -1
  const onePageOnly = args.onePageOnly ?? true
  const orderBy = args.orderBy ?? COLUMN_NAMES.ItemModified
  const ascending = args.isAscending ?? false
  const query = list.query().filter(filter).orderBy([{ ascending, orderBy }])
  const startAtPageQuery = pageNum > -1 ? query.skip(pageNum * PAGE_SIZE) : query
  const oneOrManyPagesQuery = onePageOnly ? startAtPageQuery.top(PAGE_SIZE) : startAtPageQuery

  const items = await oneOrManyPagesQuery.get()
  const convertedItems = [...items].map((i) => convertDatesInServerObject(i))
  return convertedItems
}

const getDueOutCount = async (args: DueOutFetchArgs): Promise<DueOutCount[]> => {
  const list =args.isViewApplied
  ? await mpfRViewsHttp().getview(VIEW_NAME)
  :  await mpfRListsHttp().getList(LIST_NAME)
  const filter = constructDueOutFilter(args , args.isViewApplied)

  const items = await list
    .query()
    .filter(filter)
    .select(COLUMN_NAMES.DueOutGUID)
    .count(COLUMN_NAMES.DueOutGUID)
    .get()

  return [...items] as DueOutCount[]
}


/**
 *
 * @param args DueOutFetchArgs specifying the filter to apply to the DueOuts
 *             list.  Must also define the "distinctFiltersToQuery" which is
 *             an array of field names from which to obtain the distinct values.
 * @returns
 */
const getDueOutDistinctValues = async (
  args: DueOutFetchArgs
): Promise<Map<string, number[] | string[]>[]> => {
  const fieldNames = args?.distinctFiltersToQuery ?? []
  const list = args.isViewApplied ?  await mpfRViewsHttp().getview(VIEW_NAME) :
  await mpfRListsHttp().getList(LIST_NAME)
 const filter = constructDueOutFilter(args , args.isViewApplied)

  const doOneQuery = async (fieldName) => {
    const query = list.query().filter(filter).select(fieldName).distinct(true)    
    const result = await query.get()
    return [...result].map((r) => r[fieldName])
  }

  const queries = fieldNames.map((field) => doOneQuery(field))
  const allResults = await Promise.all(queries)

  const returnMap = new Map<string, number[] | string[]>()

  if (allResults.length === fieldNames.length) {
    for (let i = 0; i < fieldNames.length; i++) {
      returnMap.set(fieldNames[i], allResults[i])
    }
  }
  return [returnMap]
}

const addOrUpdateDueOut = async (item) => {
  try {
    const list = await mpfRListsHttp().getList(LIST_NAME)

    const result = await list.addOrUpdateItem(item)

    return result as DueOut
  } catch (error) {
    console.error('An error occurred while adding or updating the dueout:', error)
    throw new Error('Failed to add or update dueout')
  }
}

const convertDatesInServerObject = (item: IRListItem): DueOut => {
  const dueout = item as DueOut
  const todate = (d) => (d ? new Date(d) : null)

  return {
    ...dueout,
    CanceledDate: todate(dueout.CanceledDate),
    ClosedDate: todate(dueout.ClosedDate),
    DueDate: todate(dueout.DueDate),
    ItemCreated: todate(dueout.ItemCreated) ?? new Date(),
    ItemModified: todate(dueout.ItemModified) ?? new Date(),
  }
}

const constructDueOutFilter = (args: DueOutFetchArgs, isView?: boolean): string => {
  const filters: string[] = []
  const today = new Date()
  today.setHours(0, 0, 0, 0)

  filters.push(isArchivedFilter(args.isArchivedFilter ?? null))
  if (args.searchFilter && args.searchFilter !== ALL_SELECT_VALUE_STRING && isView) {
    //This will need to change in the future from startswith to contains, however currently contains is yet to be implemented.
    filters.push(
      `(contains(DueOutID, '${formatStringForServer(
        args.searchFilter,
        'single-quotes'
      )}') or contains(Subject, '${formatStringForServer(
        args.searchFilter,
        'single-quotes'
      )}') or contains(DutyEmail, '${formatStringForServer(
        args.searchFilter,
        'single-quotes'
      )}')or contains(DoDNumber, '${formatStringForServer(
        args.searchFilter,
        'single-quotes'
      )}') or contains(CustomerName, '${formatStringForServer(
        args.searchFilter,
        'single-quotes'
      )}'))`
    )
  } else if (args.searchFilter && args.searchFilter !== ALL_SELECT_VALUE_STRING) {
    //This will need to change in the future from startswith to contains, however currently contains is yet to be implemented.
    filters.push(
      `(contains(DueOutID, '${formatStringForServer(
        args.searchFilter,
        'single-quotes'
      )}') or contains(Subject, '${formatStringForServer(
        args.searchFilter,
        'single-quotes'
      )}') or contains(DutyEmail, '${formatStringForServer(args.searchFilter, 'single-quotes')}'))`
    )
  }
  if (args.archivedsearchFilter && args.archivedsearchFilter !== ALL_SELECT_VALUE_STRING) {
    filters.push(
      `(contains(DueOutID, '${formatStringForServer(
        args.archivedsearchFilter,
        'single-quotes'
      )}') or contains(ServicingOrganization, '${formatStringForServer(
        args.archivedsearchFilter,
        'single-quotes'
      )}') or contains(Subject, '${formatStringForServer(
        args.archivedsearchFilter,
        'single-quotes'
      )}')  or contains(SectionValue, '${formatStringForServer(
        args.archivedsearchFilter,
        'single-quotes'
      )}')  or contains(CustomerName, '${formatStringForServer(
        args.archivedsearchFilter,
        'single-quotes'
      )}') or contains(CategoryValue, '${formatStringForServer(
        args.archivedsearchFilter,
        'single-quotes'
      )}') or contains(SubCategoryValue, '${formatStringForServer(
        args.archivedsearchFilter,
        'single-quotes'
      )}') or contains(DutyEmail, '${formatStringForServer(
        args.archivedsearchFilter,
        'single-quotes'
      )}'))`
    )
  }
  if (args.relatedUserFilter) {
    const userFilter = or(
      `Customer eq '${args.relatedUserFilter}'`,
      `ItemCreatedBy eq '${args.relatedUserFilter}'`
    )
    filters.push(userFilter)
  }

  if (args.yearFilter && args.yearFilter !== ALL_SELECT_VALUE) {
    filters.push(`FY eq ${args.yearFilter}`)
  }


  if (args.userLoginRole && 
    Object.values(args.userLoginRole).some(arr => Array.isArray(arr) && arr.length > 0)) {
      const roleFilters : string[] = []  
      const componentFilters : string[] = []    
   if (
    Array.isArray(args.userLoginRole.usersAssignedComponentCode) &&
    args.userLoginRole.usersAssignedComponentCode.length > 0
  ) {
    const assignCompFilter = args.userLoginRole.usersAssignedComponentCode.map(
      (component) => `AssignedComponentCode eq ${component}`
    )
    roleFilters.push(or(...assignCompFilter))
    componentFilters.push(or(...assignCompFilter))
  }
  if ( componentFilters.length === 0 &&
    Array.isArray(args.userLoginRole.usersMajcomCodeFilter) &&
    args.userLoginRole.usersMajcomCodeFilter.length > 0
  ) {
    const assignCompFilter = args.userLoginRole.usersMajcomCodeFilter.map(
      (majcom) => `ServicingMAJCOMCode eq ${majcom}`
    )
    roleFilters.push(or(...assignCompFilter))
  }
  if (
    componentFilters.length === 0 &&
    Array.isArray(args.userLoginRole.usersServicingOrganizationFilter) &&
    Array.isArray(args.userLoginRole.usersSectionCodeFilter) &&
    args.userLoginRole.usersServicingOrganizationFilter.length > 0 &&
    args.userLoginRole.usersSectionCodeFilter.length > 0
  ) {
    const servicingOrgFilter = args.userLoginRole.usersServicingOrganizationFilter.map((s, i) => {
      const sectionCode = args?.userLoginRole?.usersSectionCodeFilter?.[i]
      return `(ServicingOrganization eq '${s}' and SectionCode eq ${sectionCode ?? 'null'})`
    })  
    roleFilters.push(or(...servicingOrgFilter))
  }
  if (
    componentFilters.length === 0 &&
    Array.isArray(args.userLoginRole.usersServicingOrganizationWithoutSectionFilter) &&
    args.userLoginRole.usersServicingOrganizationWithoutSectionFilter.length > 0 
  ) {
    const servicingOrgFilter =
     args.userLoginRole.usersServicingOrganizationWithoutSectionFilter.map((s) => {
      return `(ServicingOrganization eq '${s}')`
    })
  
    roleFilters.push(or(...servicingOrgFilter))
  }  
  
  if (Array.isArray(args.userLoginRole.CSSPASCODEFilter) && 
       args.userLoginRole.CSSPASCODEFilter.length > 0 ) {
    const cssPascodeFilter = args.userLoginRole.CSSPASCODEFilter.map((pascode) => `PASCODE eq '${pascode}' and ServicingBy eq 'CSS'`)
    roleFilters.push(or(...cssPascodeFilter))
  }
   filters.push(or(...roleFilters))

}

  if (
    args.assignedComponentCodeFilter  &&
    args.assignedComponentCodeFilter !== ALL_SELECT_VALUE
  ) {
    filters.push(`AssignedComponentCode eq ${args.assignedComponentCodeFilter}`)
  }

  if (args.servicingMAJCOMCodeFilter && args.servicingMAJCOMCodeFilter !== ALL_SELECT_VALUE) {
    filters.push(`ServicingMAJCOMCode eq ${args.servicingMAJCOMCodeFilter}`)
  }

  if (
    args.servicingOrganizationFilter &&
    args.servicingOrganizationFilter !== ALL_SELECT_VALUE_STRING
  ) {
    filters.push(`ServicingOrganization eq '${args.servicingOrganizationFilter}'`)
  }
  if (args.servicingByFilter && args.servicingByFilter !== ALL_SELECT_VALUE_STRING) {
    filters.push(`ServicingBy eq '${args.servicingByFilter}'`)
  }


  if ( args.sectionCodeFilter && args.sectionCodeFilter !== ALL_SELECT_VALUE ) {
    filters.push(`SectionCode eq ${args.sectionCodeFilter}`)
  }
  if (args.categoryCodeFilter && args.categoryCodeFilter !== ALL_SELECT_VALUE) {
    filters.push(`CategoryCode eq ${args.categoryCodeFilter}`)
  }
  if (args.subCategoryCodeFilter !== undefined && args.subCategoryCodeFilter !== ALL_SELECT_VALUE) {
    filters.push(`SubCategoryCode eq ${args.subCategoryCodeFilter}`)
  }
  if (args.priorityFilter && args.priorityFilter !== ALL_SELECT_VALUE_STRING) {
    filters.push(`Priority eq '${args.priorityFilter}'`)
  }
  if (args.dueOutPRPFilter && args.dueOutPRPFilter !== ALL_SELECT_VALUE_STRING) {
    filters.push(`PRP eq '${args.dueOutPRPFilter}'`)
  }
  if (args.submittedFilter && args.submittedFilter !== ALL_SELECT_VALUE_STRING) {
    filters.push(
      `ItemCreated ${args.submittedFilter === 'More than 30 days' ? 'lt' : 'ge'} ${getFilteredDates(
        args.submittedFilter as FilteredDatesType
      )}`
    )
  }
  if (args.archivedFilter && args.archivedFilter !== ALL_SELECT_VALUE_STRING) {
    filters.push(
      `ArchivedDate ${args.archivedFilter === 'More than 30 days' ? 'lt' : 'ge'} ${getFilteredDates(
        args.archivedFilter as FilteredDatesType
      )}`
    )
  }
  if (args.closedFilter && args.closedFilter !== ALL_SELECT_VALUE_STRING) {
    filters.push(
      `ClosedDate ${args.closedFilter === 'More than 30 days' ? 'lt' : 'ge'} ${getFilteredDates(
        args.closedFilter as FilteredDatesType
      )}`
    )
  }
  if (args.dueOutRNLTDFilter && args.dueOutRNLTDFilter !== ALL_SELECT_VALUE_STRING) {
    filters.push(`RNLTD ge ${today.toISOString()} and RNLTD lt ${getFilteredDates(args.dueOutRNLTDFilter as FilteredDatesType)}`)
  }
  if (args.dueOutPDDFilter && args.dueOutPDDFilter !== ALL_SELECT_VALUE_STRING) {
    filters.push(`PDD ge ${today.toISOString()} and PDD lt ${getFilteredDates(args.dueOutPDDFilter as FilteredDatesType)}`)
  }
  if (args.dueOutDOSFilter && args.dueOutDOSFilter !== ALL_SELECT_VALUE_STRING) {
    filters.push(`DOS ge ${today.toISOString()} and DOS lt ${getFilteredDates(args.dueOutDOSFilter as FilteredDatesType)}`)
  }
  if (args.dueOutDueDateFilter && args.dueOutDueDateFilter !== ALL_SELECT_VALUE_STRING) {
    filters.push(`DueDate ge ${today.toISOString()} and DueDate lt ${getFilteredDates(args.dueOutDueDateFilter as FilteredDatesType)}`)
  }
  if (args.threeDaysAgoDueDateCount) {
    const todayDate = new Date()
    const startDate = new Date()
    const endDate = new Date()
    startDate.setDate(todayDate.getDate() - 3)
    endDate.setDate(todayDate.getDate() + 0)
    filters.push(`DueDate ge ${startDate.toISOString()} and DueDate le ${endDate.toISOString()}`)
  }
  if (args.sevevenDaysAgoDueDateCount) {
    const sevenDaysAgo = new Date()
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7)
    const fourDaysAgo = new Date()
    fourDaysAgo.setDate(fourDaysAgo.getDate() - 4)
    filters.push(
      `DueDate gt ${sevenDaysAgo.toISOString()} and DueDate le ${fourDaysAgo.toISOString()}`
    )
  }
  if (args.tenDaysAgoDueDateCount) {
    const tenDaysAgo = new Date()
    tenDaysAgo.setDate(tenDaysAgo.getDate() - 10)
    const sevenDaysAgo = new Date()
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7)
    filters.push(
      `DueDate gt ${tenDaysAgo.toISOString()} and DueDate le ${sevenDaysAgo.toISOString()}`
    )
  }
  if (args.morethanTenDaysAgoDueDateCount) {
    const tenDaysAgo = new Date()
    tenDaysAgo.setDate(tenDaysAgo.getDate() - 10)
    filters.push(`DueDate lt ${tenDaysAgo.toISOString()}`)
  }
  if (args.dueoutGuid) {
    filters.push(`DueOutGUID eq '${args.dueoutGuid}'`)
  }

  if (args.statusCodesFilter && args.statusCodesFilter.length > 0) {
    const terms = args.statusCodesFilter.map((s) => `StatusCode eq '${s}'`)
    filters.push(or(...terms))
  }

  if (args.statusCodesOmittedFilter) {
    for (const statusCode of args.statusCodesOmittedFilter) {
      filters.push(`StatusCode ne '${statusCode}'`)
    }
  }

  if (args.dueDateLessThanDateIgnoreTime) {
    const dateWithoutTime = new Date(args.dueDateLessThanDateIgnoreTime)
    dateWithoutTime.setHours(0, 0, 0, 0)
    filters.push(`DueDate lt ${dateWithoutTime.toISOString()}`)
  }
  if (args.myDueouts) {
    filters.push(`(ItemCreatedBy eq '${args.myDueouts}' or Customer eq '${args.myDueouts}') `)
    if (args.savedFilter)
      filters.push(
        `(ItemCreatedBy eq '${args.myDueouts}' and StatusCode eq '${STATUS_CODE.SAVED}' )`
      )
    else filters.push(`StatusCode ne '${STATUS_CODE.SAVED}'`)
  }
  if (args.savedFilter === false) {
    filters.push(`StatusCode ne '${STATUS_CODE.SAVED}'`)
  }
  if (args.hasClosedAndCanceled === false) {
    filters.push(
      `StatusCode ne '${STATUS_CODE.CANCELED}'
        and StatusCode ne '${STATUS_CODE.CLOSED}' 
      `
    )
  }
  const filter = and(...filters)
  return filter
}

const getDueoutGroupByCounts = async (args: any): Promise<any> => {
  const list = await mpfRListsHttp().getList(LIST_NAME)
  const filter = constructDueOutFilter(args)

  const formattedCategories = await list
    .query()
    .filter(filter)
    .select(COLUMN_NAMES.DueOutGUID, args.groupBy)
    .groupBy(args.groupBy)
    .count(COLUMN_NAMES.DueOutGUID)
    .get()

  return formattedCategories.sort((a, b) => b.DueOutGUID_count - a.DueOutGUID_count)
}

const getDueoutStatusCounts = async (args: any): Promise<any> => {
  const list = args.isViewApplied
  ? await mpfRViewsHttp().getview(VIEW_NAME)
  : await mpfRListsHttp().getList(LIST_NAME)
  const filter = constructDueOutFilter(args, args.isViewApplied)

  const formattedCategories = await list
    .query()
    .filter(filter)
    .select(COLUMN_NAMES.DueOutGUID, COLUMN_NAMES.StatusCode)
    .groupBy(COLUMN_NAMES.StatusCode)
    .count(COLUMN_NAMES.DueOutGUID)
    .get()

  return formattedCategories.sort((a, b) => b.DueOutGUID_count - a.DueOutGUID_count)
}
const deleteDueout = async (ekey, eTag) => {
  try {
    const list = await mpfRListsHttp().getList(LIST_NAME)
    const result = await list.deleteItem(ekey, eTag)
    return result
  } catch (error) {
    console.error('An error occurred while adding or updating the inquiry:', error)
    throw new Error('Failed to add or update inquiry')
  }
}
export {
  COLUMN_NAMES,
  type DueOut,
  type DueOutCategoryCount,
  type DueOutCount,
  type DueOutDueDateCount,
  type DueOutFetchArgs,
  type DueOutPriorityCount,
  type DueOutStatusCount,
  LIST_NAME,
  addOrUpdateDueOut,
  deleteDueout,
  getDueOutCount,
  getDueOutDistinctValues,
  getDueOuts,
  getDueOutsViews,
  getDueoutGroupByCounts,
  getDueoutStatusCounts
}
